/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";

type SelectionMode = "explicit" | "all" | "allExcept";

type UseTableRowMultiPageSelectionParams = {
  currentPageIds: string[];
  totalCount: number;
};

type UseTableRowMultiPageSelectionReturn = {
  rowSelection: RowSelectionState;
  setRowSelection: (
    value:
      | RowSelectionState
      | ((prevState: RowSelectionState) => RowSelectionState)
  ) => void;
  selectionMode: SelectionMode;
  areAllItemsSelected: boolean;
  autoSelectAcrossPages: boolean;
  selectedItemIds: string[];
  selectedItemCountAcrossPages: number;
  setSelectedItemIds: (ids: string[]) => void;
  clearSelection: () => void;
  selectAllItems: (allItemIds?: string[]) => void;
};

export function useTableRowMultiPageSelection({
  currentPageIds,
  totalCount,
}: UseTableRowMultiPageSelectionParams): UseTableRowMultiPageSelectionReturn {
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [rowSelection, setRowSelectionState] = useState<RowSelectionState>({});
  const [selectionMode, setSelectionMode] = useState<SelectionMode>("explicit");
  const [excludedItemIds, setExcludedItemIds] = useState<string[]>([]);

  const areAllItemsSelected = selectionMode === "all";
  const autoSelectAcrossPages = selectionMode === "allExcept";

  const clearSelection = () => {
    setRowSelectionState({});
    setSelectionMode("explicit");
    setExcludedItemIds([]);
    setSelectedItemIds([]);
  };

  const appendExcludedItemIds = (ids: string[]) => {
    setExcludedItemIds((previousExcludedIds) =>
      Array.from(new Set([...previousExcludedIds, ...ids]))
    );
  };

  // Wrap setRowSelection with multi-page logic
  const setRowSelection = (
    value:
      | RowSelectionState
      | ((prevState: RowSelectionState) => RowSelectionState)
  ) => {
    setRowSelectionState((previousSelection) => {
      const nextSelection =
        typeof value === "function" ? value(previousSelection) : value;
      const deselectedOnCurrentPage = currentPageIds.filter(
        (id) => !!previousSelection[id] && !nextSelection[id]
      );
      const selectedOnCurrentPage = currentPageIds.filter(
        (id) => !previousSelection[id] && !!nextSelection[id]
      );
      if (areAllItemsSelected && deselectedOnCurrentPage.length > 0) {
        setSelectionMode("allExcept");
        appendExcludedItemIds(deselectedOnCurrentPage);
        return nextSelection;
      }
      if (!autoSelectAcrossPages) {
        return nextSelection;
      }
      if (deselectedOnCurrentPage.length > 0) {
        appendExcludedItemIds(deselectedOnCurrentPage);
      }
      if (selectedOnCurrentPage.length > 0) {
        setExcludedItemIds((previousExcludedIds) => {
          const nextExcludedIds = previousExcludedIds.filter(
            (id) => !selectedOnCurrentPage.includes(id)
          );
          if (nextExcludedIds.length === 0) {
            setSelectionMode("all");
          }
          return nextExcludedIds;
        });
      }
      return nextSelection;
    });
  };
  // Auto-sync selection when page changes
  useEffect(() => {
    if (!areAllItemsSelected && !autoSelectAcrossPages) {
      return;
    }
    setRowSelectionState((previousSelection) => {
      const excludedItemIdsSet = new Set(excludedItemIds);
      const hasUnselectedRows = currentPageIds.some((id) => {
        const shouldBeSelected = !excludedItemIdsSet.has(id);
        return Boolean(previousSelection[id]) !== shouldBeSelected;
      });
      if (!hasUnselectedRows) {
        return previousSelection;
      }
      const currentPageSelection = Object.fromEntries(
        currentPageIds.map((id) => [id, !excludedItemIdsSet.has(id)])
      );
      return {
        ...previousSelection,
        ...currentPageSelection,
      };
    });
  }, [
    areAllItemsSelected,
    autoSelectAcrossPages,
    excludedItemIds,
    currentPageIds,
  ]);

  const selectedItemCountAcrossPages = useMemo(() => {
    if (selectionMode === "all") {
      return totalCount;
    }
    if (selectionMode === "explicit") {
      return Object.keys(rowSelection).length;
    }
    return totalCount - excludedItemIds.length;
  }, [selectionMode, rowSelection, excludedItemIds, totalCount]);

  return {
    rowSelection,
    setRowSelection,
    selectionMode,
    selectedItemCountAcrossPages,
    areAllItemsSelected,
    autoSelectAcrossPages,
    selectedItemIds,
    setSelectedItemIds,
    clearSelection,
    selectAllItems: (allItemIds?: string[]) => {
      setSelectionMode("all");
      setExcludedItemIds([]);
      setSelectedItemIds(allItemIds ? allItemIds : []);

      if (allItemIds) {
        const rowSelectionState = allItemIds.reduce((state, id) => {
          return {
            ...state,
            [id]: true,
          };
        }, {});
        setRowSelection(rowSelectionState);
      }
    },
  };
}
